if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.sectionId = this.id.replace('ProductModal-', '');
        this.dialog = this.querySelector('.product-media-modal__dialog');
        this.content = this.querySelector('.product-media-modal__content');
        this.closeButton = this.querySelector('.product-media-modal__toggle');
        this.zoomButton = this.querySelector('.product-media-modal__zoom');
        this.prevButton = this.querySelector('.product-media-modal__nav--prev');
        this.nextButton = this.querySelector('.product-media-modal__nav--next');

        this.scale = 1;
        this.translateX = 0;
        this.translateY = 0;

        this.currentMedia = null;
        this.closeAnimationTimer = null;

        this.dragPointerId = null;
        this.dragStartX = 0;
        this.dragStartY = 0;
        this.dragOriginX = 0;
        this.dragOriginY = 0;
        this.dragMoved = false;
        this.ignoreClickToggle = false;
        this.pointerHandledClickToggle = false;
        this.pointerDownScale = 1;
        this.lastPointerType = 'mouse';

        this.lastTapTime = 0;
        this.lastTapX = 0;
        this.lastTapY = 0;
        this.doubleTapDelay = 280;
        this.doubleTapDistance = 24;

        this.pointers = new Map();
        this.isPinching = false;
        this.pinchStartDistance = 0;
        this.pinchStartScale = 1;

        this.mobileGesturePointerId = null;
        this.mobileGestureMode = '';
        this.mobileGestureStartX = 0;
        this.mobileGestureStartY = 0;
        this.mobileGestureDeltaX = 0;
        this.mobileGestureDeltaY = 0;
        this.mobileGestureTarget = null;
        this.mobileGestureDirection = 0;
        this.mobileGestureAnimating = false;

        this.rafId = null;
        this.pendingAnimated = false;
        this.preloadTimers = [];
        this.preloadedImages = new Map();
        this.preloadIdleHandle = null;

        this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
        this.handleVariantMediaChanged = this.handleVariantMediaChanged.bind(this);
        this.handleContentClick = this.handleContentClick.bind(this);
        this.handleWheel = this.handleWheel.bind(this);
        this.handlePointerDown = this.handlePointerDown.bind(this);
        this.handlePointerMove = this.handlePointerMove.bind(this);
        this.handlePointerUp = this.handlePointerUp.bind(this);
        this.handleZoomButtonClick = this.handleZoomButtonClick.bind(this);

        const stopBubble = (event) => {
          event.stopPropagation();
        };

        this.dialog?.addEventListener('click', stopBubble);
        this.dialog?.addEventListener('pointerup', stopBubble);
        this.content?.addEventListener('click', stopBubble);
        this.content?.addEventListener('pointerup', stopBubble);

        this.content?.addEventListener('click', this.handleContentClick);
        this.content?.addEventListener('wheel', this.handleWheel, { passive: false });
        this.content?.addEventListener('pointerdown', this.handlePointerDown, { passive: false });
        this.content?.addEventListener('pointermove', this.handlePointerMove, { passive: false });
        this.content?.addEventListener('pointerup', this.handlePointerUp);
        this.content?.addEventListener('pointercancel', this.handlePointerUp);
        this.content?.addEventListener('pointerleave', this.handlePointerUp);

        this.zoomButton?.addEventListener('click', this.handleZoomButtonClick);

        this.prevButton?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (this.mobileGestureAnimating) return;
          this.showPreviousMedia();
        });

        this.nextButton?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (this.mobileGestureAnimating) return;
          this.showNextMedia();
        });

        document.addEventListener('variant-media:changed', this.handleVariantMediaChanged);
      }

      disconnectedCallback() {
        this.clearPreloadTimers();
        this.preloadedImages.clear();
        document.removeEventListener('keydown', this.handleDocumentKeydown);
        document.removeEventListener('variant-media:changed', this.handleVariantMediaChanged);
      }

      isMobileView() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      handleZoomButtonClick(event) {
        event.preventDefault();
        event.stopPropagation();

        const img = this.getActiveImage();
        if (!img) return;

        this.setZoom(this.scale > 1 ? 1 : 2.2, true);
      }

      resetTapTracking() {
        this.lastTapTime = 0;
        this.lastTapX = 0;
        this.lastTapY = 0;
      }

      handlePossibleDoubleTap(event) {
        if (event.pointerType === 'mouse') return false;
        if (!this.isMobileView()) return false;
        if (this.mobileGestureAnimating || this.isPinching) return false;
        if (event.target.closest('.product-media-modal__nav, .product-media-modal__toggle, .product-media-modal__zoom')) {
          this.resetTapTracking();
          return false;
        }

        const img = this.getActiveImage();
        if (!img) {
          this.resetTapTracking();
          return false;
        }

        const now = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
        const timeSinceLastTap = now - this.lastTapTime;
        const distanceSinceLastTap = Math.hypot(event.clientX - this.lastTapX, event.clientY - this.lastTapY);

        const isDoubleTap =
          this.lastTapTime > 0 &&
          timeSinceLastTap <= this.doubleTapDelay &&
          distanceSinceLastTap <= this.doubleTapDistance;

        this.lastTapTime = now;
        this.lastTapX = event.clientX;
        this.lastTapY = event.clientY;

        if (!isDoubleTap) return false;

        this.resetTapTracking();
        this.ignoreClickToggle = false;
        this.pointerHandledClickToggle = true;
        this.setZoom(this.scale > 1 ? 1 : 2.2, true);
        return true;
      }

      resetMobileGestureTracking() {
        this.mobileGesturePointerId = null;
        this.mobileGestureMode = '';
        this.mobileGestureStartX = 0;
        this.mobileGestureStartY = 0;
        this.mobileGestureDeltaX = 0;
        this.mobileGestureDeltaY = 0;
      }

      cleanupSwipePreview() {
        if (!this.content) return;

        Array.from(this.content.children).forEach((item) => {
          item.style.transition = '';
          item.style.transform = '';
          item.style.opacity = '';

          if (item !== this.currentMedia && item.classList.contains('is-swipe-target')) {
            item.classList.remove('active', 'is-swipe-target');
          }
        });

        this.mobileGestureTarget = null;
        this.mobileGestureDirection = 0;
      }

      clearDialogGestureStyles() {
        if (this.dialog) {
          this.dialog.style.transition = '';
          this.dialog.style.transform = '';
          this.dialog.style.opacity = '';
          this.dialog.style.background = '';
        }

        if (this.content) {
          this.content.style.transition = '';
          this.content.style.transform = '';
          this.content.style.opacity = '';
          this.content.style.borderRadius = '';
          this.content.style.boxShadow = '';
          this.content.style.transformOrigin = '';
        }

        [this.closeButton, this.zoomButton].forEach((button) => {
          if (!button) return;
          button.style.transition = '';
          button.style.opacity = '';
          button.style.transform = '';
        });
      }

      getHorizontalSwipeThreshold() {
        const width = this.content?.clientWidth || window.innerWidth || 320;
        return Math.max(42, Math.min(96, width * 0.16));
      }

      getVerticalDismissThreshold() {
        const height = window.innerHeight || this.content?.clientHeight || 640;
        return Math.max(90, Math.min(180, height * 0.14));
      }

      getGestureTarget(direction) {
        const items = this.getMediaItems();
        if (items.length < 2) return null;

        const currentIndex = Math.max(0, items.findIndex((item) => item === this.currentMedia));
        const targetIndex =
          direction > 0
            ? currentIndex >= items.length - 1
              ? 0
              : currentIndex + 1
            : currentIndex <= 0
              ? items.length - 1
              : currentIndex - 1;

        return items[targetIndex] || null;
      }

      ensureGestureTarget(direction) {
        const target = this.getGestureTarget(direction);
        if (!target) return null;

        if (this.mobileGestureTarget && this.mobileGestureTarget !== target) {
          this.mobileGestureTarget.style.transition = '';
          this.mobileGestureTarget.style.transform = '';
          this.mobileGestureTarget.style.opacity = '';
          this.mobileGestureTarget.classList.remove('active', 'is-swipe-target');
        }

        this.mobileGestureTarget = target;
        this.mobileGestureDirection = direction;

        if (target !== this.currentMedia) {
          target.classList.add('active', 'is-swipe-target');
        }

        return target;
      }

      updateHorizontalSwipe(deltaX) {
        if (!this.currentMedia || !this.content) return;

        const direction = deltaX < 0 ? 1 : -1;
        const target = this.ensureGestureTarget(direction);
        if (!target) return;

        this.warmMediaItem(this.currentMedia, true);
        this.warmMediaItem(target, true);

        const width = this.content.clientWidth || window.innerWidth || 1;
        const targetOffset = direction > 0 ? width : -width;
        const activeOpacity = 1 - Math.min(0.16, (Math.abs(deltaX) / width) * 0.16);

        this.currentMedia.style.transition = 'none';
        this.currentMedia.style.transform = `translate3d(${deltaX}px, 0, 0)`;
        this.currentMedia.style.opacity = String(activeOpacity);

        target.style.transition = 'none';
        target.style.transform = `translate3d(${targetOffset + deltaX}px, 0, 0)`;
        target.style.opacity = '1';
      }

      finishHorizontalSwipe(shouldChange) {
        if (!this.currentMedia || !this.content || !this.mobileGestureTarget) {
          this.cleanupSwipePreview();
          return;
        }

        const current = this.currentMedia;
        const target = this.mobileGestureTarget;
        const direction = this.mobileGestureDirection;
        const width = this.content.clientWidth || window.innerWidth || 1;
        const timing = '320ms cubic-bezier(0.22, 1, 0.36, 1)';

        this.warmMediaItem(current, true);
        this.warmMediaItem(target, true);

        this.mobileGestureAnimating = true;

        current.style.willChange = 'transform, opacity';
        target.style.willChange = 'transform, opacity';

        current.style.transition = `transform ${timing}, opacity ${timing}`;
        target.style.transition = `transform ${timing}, opacity ${timing}`;

        if (shouldChange) {
          current.style.transform = `translate3d(${direction > 0 ? -width : width}px, 0, 0)`;
          current.style.opacity = '0.94';
          target.style.transform = 'translate3d(0, 0, 0)';
          target.style.opacity = '1';

          window.setTimeout(() => {
            const targetId = this.getMediaId(target);
            this.mobileGestureAnimating = false;
            current.style.willChange = '';
            target.style.willChange = '';
            this.cleanupSwipePreview();
            this.showActiveMedia(targetId);
          }, 320);

          return;
        }

        current.style.transform = 'translate3d(0, 0, 0)';
        current.style.opacity = '1';
        target.style.transform = `translate3d(${direction > 0 ? width : -width}px, 0, 0)`;
        target.style.opacity = '1';

        window.setTimeout(() => {
          this.mobileGestureAnimating = false;
          current.style.willChange = '';
          target.style.willChange = '';
          this.cleanupSwipePreview();
        }, 320);
      }

      updateVerticalDismiss(deltaY) {
        if (!this.dialog || !this.content) return;

        const viewportHeight = window.innerHeight || 800;
        const progress = Math.min(1, Math.abs(deltaY) / (viewportHeight * 0.9));
        const eased = 1 - Math.pow(1 - progress, 2);

        const translateY = deltaY * 0.92;
        const scale = 1 - eased * 0.07;
        const opacity = 1 - eased * 0.12;
        const radius = Math.min(28, eased * 28);
        const buttonOpacity = 1 - eased * 0.45;

        this.dialog.style.transition = 'none';
        this.dialog.style.transform = 'translate3d(0, 0, 0)';
        this.dialog.style.opacity = '1';

        this.content.style.transition = 'none';
        this.content.style.transformOrigin = 'center center';
        this.content.style.transform = `translate3d(0, ${translateY}px, 0) scale(${scale})`;
        this.content.style.opacity = String(opacity);
        this.content.style.borderRadius = `${radius}px`;
        this.content.style.boxShadow = `0 2.4rem 6rem rgba(0, 0, 0, ${0.08 + eased * 0.14})`;

        [this.closeButton, this.zoomButton].forEach((button) => {
          if (!button) return;
          button.style.transition = 'none';
          button.style.opacity = String(buttonOpacity);
          button.style.transform = `translate3d(0, ${deltaY * 0.18}px, 0)`;
        });
      }

      finishVerticalDismiss(shouldClose) {
        if (!this.dialog || !this.content) return;

        const timing = '240ms cubic-bezier(0.22, 1, 0.36, 1)';
        const viewportHeight = window.innerHeight || 800;
        const travel = this.mobileGestureDeltaY < 0 ? -viewportHeight * 0.72 : viewportHeight * 0.72;

        if (shouldClose) {
          this.content.style.transition = `transform ${timing}, opacity ${timing}, border-radius ${timing}, box-shadow ${timing}`;
          this.content.style.transform = `translate3d(0, ${travel}px, 0) scale(0.9)`;
          this.content.style.opacity = '0';
          this.content.style.borderRadius = '2.8rem';
          this.content.style.boxShadow = '0 3rem 8rem rgba(0, 0, 0, 0.08)';

          [this.closeButton, this.zoomButton].forEach((button) => {
            if (!button) return;
            button.style.transition = `opacity ${timing}, transform ${timing}`;
            button.style.opacity = '0';
            button.style.transform = `translate3d(0, ${travel * 0.18}px, 0)`;
          });

          window.setTimeout(() => {
            this.clearDialogGestureStyles();
            this.hide();
          }, 240);

          return;
        }

        this.content.style.transition = `transform ${timing}, opacity ${timing}, border-radius ${timing}, box-shadow ${timing}`;
        this.content.style.transform = 'translate3d(0, 0, 0) scale(1)';
        this.content.style.opacity = '1';
        this.content.style.borderRadius = '0';
        this.content.style.boxShadow = 'none';

        [this.closeButton, this.zoomButton].forEach((button) => {
          if (!button) return;
          button.style.transition = `opacity ${timing}, transform ${timing}`;
          button.style.opacity = '1';
          button.style.transform = 'translate3d(0, 0, 0)';
        });

        window.setTimeout(() => {
          this.clearDialogGestureStyles();
        }, 240);
      }

      show(opener) {
        clearTimeout(this.closeAnimationTimer);
        this.classList.remove('is-closing');
        this.clearDialogGestureStyles();
        this.cleanupSwipePreview();
        this.resetMobileGestureTracking();
        this.resetTapTracking();

        super.show(opener);
        document.addEventListener('keydown', this.handleDocumentKeydown);

        requestAnimationFrame(() => {
          this.showActiveMedia(opener?.getAttribute('data-media-id'));
          this.content?.focus();
        });
      }

      hide() {
        document.removeEventListener('keydown', this.handleDocumentKeydown);
        this.clearPreloadTimers();
        this.resetZoom();
        this.clearDialogGestureStyles();
        this.resetTapTracking();

        if (!this.hasAttribute('open')) return;
        if (this.classList.contains('is-closing')) return;

        this.classList.add('is-closing');

        clearTimeout(this.closeAnimationTimer);
        this.closeAnimationTimer = window.setTimeout(() => {
          this.classList.remove('is-closing');
          super.hide();
        }, 220);
      }

      normalizeMediaId(value) {
        const str = String(value || '');
        const match = str.match(/(\d+)(?!.*\d)/);
        return match ? match[1] : str;
      }

      getMediaId(item) {
        if (!item) return '';
        return this.normalizeMediaId(item.getAttribute('data-media-id') || item.id || '');
      }

      getMediaNode(item) {
        if (!item) return null;

        return (
          item.querySelector('deferred-media, product-model, model-viewer, img, video, iframe, [data-media-id]') || item
        );
      }

      getMediaItems() {
        if (!this.content) return [];

        return Array.from(this.content.children).filter((item) => {
          return this.getMediaId(item) && !item.classList.contains('variant-media-hidden');
        });
      }

      cancelPreloadIdle() {
        if (!this.preloadIdleHandle) return;

        if ('cancelIdleCallback' in window) {
          window.cancelIdleCallback(this.preloadIdleHandle);
        } else {
          window.clearTimeout(this.preloadIdleHandle);
        }

        this.preloadIdleHandle = null;
      }

      clearPreloadTimers() {
        this.preloadTimers.forEach((timer) => window.clearTimeout(timer));
        this.preloadTimers = [];
        this.cancelPreloadIdle();
      }

      getImageWarmKey(img) {
        return [
          img.currentSrc || img.getAttribute('src') || '',
          img.getAttribute('srcset') || '',
          img.getAttribute('sizes') || ''
        ].join('||');
      }

      getImageFromMediaItem(item) {
        if (!item) return null;

        const mediaNode = this.getMediaNode(item);
        if (!mediaNode) return null;

        if (mediaNode.matches?.('img')) return mediaNode;

        const inlineImage = mediaNode.querySelector?.('img');
        if (inlineImage) return inlineImage;

        const templateImage = mediaNode.querySelector?.('template')?.content?.querySelector?.('img');
        return templateImage || null;
      }

      warmImage(img, immediate = false) {
        if (!img) return;

        const src = img.currentSrc || img.getAttribute('src');
        const srcset = img.getAttribute('srcset');
        if (!src && !srcset) return;

        img.loading = 'eager';
        img.decoding = immediate ? 'sync' : 'async';

        if ('fetchPriority' in img) {
          img.fetchPriority = 'high';
        }

        if (typeof img.decode === 'function') {
          img.decode().catch(() => {});
        }

        const key = this.getImageWarmKey(img);
        if (this.preloadedImages.has(key)) return;

        const preloader = new Image();
        preloader.decoding = immediate ? 'sync' : 'async';

        if ('fetchPriority' in preloader) {
          preloader.fetchPriority = 'high';
        }

        if (srcset) preloader.srcset = srcset;

        const sizes = img.getAttribute('sizes');
        if (sizes) preloader.sizes = sizes;

        if (src) preloader.src = src;

        this.preloadedImages.set(key, preloader);

        if (typeof preloader.decode === 'function') {
          preloader.decode().catch(() => {});
        }
      }

      warmMediaItem(item, immediate = false) {
        const img = this.getImageFromMediaItem(item);
        if (!img) return;

        this.warmImage(img, immediate);
      }

      warmVisibleMediaQueue(anchorItem = this.currentMedia) {
        const items = this.getMediaItems();
        if (!items.length) return;

        this.clearPreloadTimers();

        const anchorIndex = Math.max(0, items.findIndex((item) => item === anchorItem));

        const immediateIndexes = Array.from(
          new Set([
            anchorIndex,
            (anchorIndex + 1) % items.length,
            (anchorIndex - 1 + items.length) % items.length,
            (anchorIndex + 2) % items.length,
            (anchorIndex - 2 + items.length) % items.length
          ])
        );

        immediateIndexes.forEach((index) => {
          const item = items[index];
          if (item) this.warmMediaItem(item, true);
        });

        const deferredItems = items.filter((_, index) => !immediateIndexes.includes(index));

        const runDeferredWarmup = () => {
          deferredItems.forEach((item, index) => {
            const timer = window.setTimeout(() => {
              this.warmMediaItem(item, false);
            }, index * 12);

            this.preloadTimers.push(timer);
          });
        };

        if ('requestIdleCallback' in window) {
          this.preloadIdleHandle = window.requestIdleCallback(() => {
            this.preloadIdleHandle = null;
            runDeferredWarmup();
          }, { timeout: 300 });
        } else {
          this.preloadIdleHandle = window.setTimeout(() => {
            this.preloadIdleHandle = null;
            runDeferredWarmup();
          }, 0);
        }
      }

      handleVariantMediaChanged(event) {
        const detail = event.detail || {};
        if (String(detail.sectionId) !== String(this.sectionId)) return;

        const items = this.getMediaItems();
        if (!items.length) return;

        const currentStillVisible =
          this.currentMedia &&
          items.includes(this.currentMedia) &&
          !this.currentMedia.classList.contains('variant-media-hidden');

        if (!currentStillVisible) {
          this.showActiveMedia(detail.activeId || detail.visibleIds?.[0] || null);
        } else {
          this.updateNavigation();
        }
      }

      showActiveMedia(mediaId = null) {
        const items = this.getMediaItems();
        if (!items.length) return;

        const requestedId = this.normalizeMediaId(
          mediaId || this.openedBy?.getAttribute('data-media-id') || ''
        );

        this.cleanupSwipePreview();
        this.clearDialogGestureStyles();
        this.resetMobileGestureTracking();
        this.resetTapTracking();

        Array.from(this.content.children).forEach((item) => {
          item.classList.remove('active', 'is-zoomed', 'is-swipe-target');
          item.style.transition = '';
          item.style.transform = '';
          item.style.opacity = '';
        });

        const activeMedia =
          items.find((item) => this.getMediaId(item) === requestedId) || items[0];

        if (!activeMedia) return;

        window.pauseAllMedia();

        this.currentMedia = activeMedia;
        activeMedia.classList.add('active');

        const activeMediaNode = this.getMediaNode(activeMedia);
        const activeMediaTemplate = activeMediaNode?.querySelector?.('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

        if (
          activeMediaNode &&
          activeMediaNode.nodeName === 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMediaNode.loadContent();
        }

        this.resetZoom(true);
        this.updateNavigation();
        this.warmVisibleMediaQueue(activeMedia);
      }

      updateNavigation() {
        const items = this.getMediaItems();
        const hasMultiple = items.length > 1;

        [this.prevButton, this.nextButton].forEach((button) => {
          if (!button) return;
          button.hidden = !hasMultiple;
          button.disabled = !hasMultiple;
        });
      }

      showPreviousMedia() {
        if (this.mobileGestureAnimating) return;

        const items = this.getMediaItems();
        if (items.length < 2) return;

        const currentIndex = Math.max(0, items.findIndex((item) => item === this.currentMedia));
        const targetIndex = currentIndex <= 0 ? items.length - 1 : currentIndex - 1;

        this.showActiveMedia(this.getMediaId(items[targetIndex]));
      }

      showNextMedia() {
        if (this.mobileGestureAnimating) return;

        const items = this.getMediaItems();
        if (items.length < 2) return;

        const currentIndex = Math.max(0, items.findIndex((item) => item === this.currentMedia));
        const targetIndex = currentIndex >= items.length - 1 ? 0 : currentIndex + 1;

        this.showActiveMedia(this.getMediaId(items[targetIndex]));
      }

      handleDocumentKeydown(event) {
        if (!this.hasAttribute('open')) return;
        if (event.target.closest('input, textarea, select')) return;

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.showPreviousMedia();
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.showNextMedia();
        }
      }

      getActiveImage() {
        const activeMedia = this.currentMedia || this.querySelector('.product-media-modal__content > .active');
        if (!activeMedia) return null;

        const mediaNode = this.getMediaNode(activeMedia);
        if (!mediaNode) return null;

        if (mediaNode.nodeName === 'DEFERRED-MEDIA' || mediaNode.nodeName === 'PRODUCT-MODEL') {
          return null;
        }

        if (mediaNode.matches?.('img')) return mediaNode;

        return activeMedia.querySelector('img');
      }

      getDistance(pointerA, pointerB) {
        const dx = pointerB.clientX - pointerA.clientX;
        const dy = pointerB.clientY - pointerA.clientY;
        return Math.hypot(dx, dy);
      }

      clampTranslation(img) {
        if (!this.content || !img) return;

        const containerRect = this.content.getBoundingClientRect();
        const renderedWidth = img.clientWidth;
        const renderedHeight = img.clientHeight;

        const maxX = Math.max(0, (renderedWidth * this.scale - containerRect.width) / 2);
        const maxY = Math.max(0, (renderedHeight * this.scale - containerRect.height) / 2);

        this.translateX = Math.min(maxX, Math.max(-maxX, this.translateX));
        this.translateY = Math.min(maxY, Math.max(-maxY, this.translateY));
      }

      renderTransform(animated = false) {
        const img = this.getActiveImage();
        if (!img) return;

        this.pendingAnimated = this.pendingAnimated || animated;
        if (this.rafId) return;

        this.rafId = requestAnimationFrame(() => {
          this.rafId = null;

          const activeImg = this.getActiveImage();
          if (!activeImg) return;

          this.clampTranslation(activeImg);

          activeImg.style.transition = this.pendingAnimated
            ? 'transform 180ms cubic-bezier(0.22, 1, 0.36, 1)'
            : 'none';

          activeImg.style.transform = `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.scale})`;

          if (this.currentMedia) {
            this.currentMedia.classList.toggle('is-zoomed', this.scale > 1);
          }

          this.pendingAnimated = false;
        });
      }

      setZoom(nextScale, animated = false) {
        const img = this.getActiveImage();
        if (!img) return;

        this.scale = Math.min(4, Math.max(1, nextScale));

        if (this.scale === 1) {
          this.translateX = 0;
          this.translateY = 0;
        }

        this.renderTransform(animated);
      }

      resetZoom(keepCurrentMedia = false) {
        this.scale = 1;
        this.translateX = 0;
        this.translateY = 0;

        this.dragPointerId = null;
        this.dragMoved = false;
        this.ignoreClickToggle = false;
        this.pointerHandledClickToggle = false;
        this.pointerDownScale = 1;
        this.lastPointerType = 'mouse';

        this.isPinching = false;
        this.pinchStartDistance = 0;
        this.pinchStartScale = 1;
        this.pointers.clear();

        this.resetMobileGestureTracking();
        this.cleanupSwipePreview();
        this.clearDialogGestureStyles();
        this.resetTapTracking();
        this.mobileGestureAnimating = false;

        const activeMedia = this.currentMedia || this.querySelector('.product-media-modal__content > .active');
        const img = this.getActiveImage();

        activeMedia?.classList.remove('is-zoomed');
        img?.classList.remove('is-dragging');

        if (img) {
          img.style.transition = 'none';
          img.style.transform = '';
        }

        if (!keepCurrentMedia) {
          this.currentMedia = null;
        }
      }

      handleContentClick(event) {
        if (!this.currentMedia || !this.currentMedia.contains(event.target)) return;
        if (event.target.closest('.product-media-modal__nav, .product-media-modal__toggle, .product-media-modal__zoom')) return;

        const img = this.getActiveImage();
        if (!img) return;

        if (this.pointerHandledClickToggle) {
          event.preventDefault();
          event.stopPropagation();
          this.pointerHandledClickToggle = false;
          return;
        }

        if (this.ignoreClickToggle) {
          event.preventDefault();
          event.stopPropagation();
          this.ignoreClickToggle = false;
          return;
        }

        if (this.isMobileView() && this.lastPointerType !== 'mouse') {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        this.setZoom(this.scale > 1 ? 1 : 2.2, true);
      }

      handleWheel(event) {
        if (!this.currentMedia || !this.currentMedia.contains(event.target)) return;

        const img = this.getActiveImage();
        if (!img) return;

        event.preventDefault();
        event.stopPropagation();

        const delta = event.deltaY < 0 ? 0.18 : -0.18;
        this.setZoom(Number((this.scale + delta).toFixed(2)), false);
      }

      handlePointerDown(event) {
        if (this.mobileGestureAnimating) return;
        if (!this.currentMedia || !this.currentMedia.contains(event.target)) return;
        if (event.target.closest('.product-media-modal__nav, .product-media-modal__toggle, .product-media-modal__zoom')) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        this.lastPointerType = event.pointerType || 'mouse';
        this.pointerDownScale = this.scale;
        this.pointerHandledClickToggle = false;

        if (event.pointerType === 'mouse') {
          this.ignoreClickToggle = false;
        }

        this.pointers.set(event.pointerId, {
          clientX: event.clientX,
          clientY: event.clientY
        });

        if (this.pointers.size === 2) {
          const [first, second] = Array.from(this.pointers.values());
          this.isPinching = true;
          this.dragPointerId = null;
          this.ignoreClickToggle = true;
          this.resetMobileGestureTracking();
          this.cleanupSwipePreview();
          this.clearDialogGestureStyles();
          this.resetTapTracking();
          this.pinchStartDistance = this.getDistance(first, second);
          this.pinchStartScale = this.scale;
          return;
        }

        if (event.pointerType !== 'mouse' && this.scale <= 1) {
          this.mobileGesturePointerId = event.pointerId;
          this.mobileGestureMode = '';
          this.mobileGestureStartX = event.clientX;
          this.mobileGestureStartY = event.clientY;
          this.mobileGestureDeltaX = 0;
          this.mobileGestureDeltaY = 0;
          this.content?.setPointerCapture?.(event.pointerId);
        }

        if (this.scale <= 1) return;

        const img = this.getActiveImage();
        if (!img) return;

        this.dragPointerId = event.pointerId;
        this.dragMoved = false;
        this.dragStartX = event.clientX;
        this.dragStartY = event.clientY;
        this.dragOriginX = this.translateX;
        this.dragOriginY = this.translateY;

        img.classList.add('is-dragging');
        this.content?.setPointerCapture?.(event.pointerId);
      }

      handlePointerMove(event) {
        if (!this.pointers.has(event.pointerId)) return;

        this.pointers.set(event.pointerId, {
          clientX: event.clientX,
          clientY: event.clientY
        });

        if (this.pointers.size === 2) {
          event.preventDefault();

          const [first, second] = Array.from(this.pointers.values());
          const distance = this.getDistance(first, second);

          if (!this.pinchStartDistance) {
            this.pinchStartDistance = distance;
            this.pinchStartScale = this.scale;
          }

          this.isPinching = true;
          this.ignoreClickToggle = true;
          this.resetMobileGestureTracking();
          this.cleanupSwipePreview();
          this.clearDialogGestureStyles();
          this.resetTapTracking();

          const factor = distance / this.pinchStartDistance;
          this.setZoom(this.pinchStartScale * factor, false);
          return;
        }

        if (this.mobileGesturePointerId === event.pointerId && this.scale <= 1 && !this.isPinching) {
          this.mobileGestureDeltaX = event.clientX - this.mobileGestureStartX;
          this.mobileGestureDeltaY = event.clientY - this.mobileGestureStartY;

          const absX = Math.abs(this.mobileGestureDeltaX);
          const absY = Math.abs(this.mobileGestureDeltaY);

          if (!this.mobileGestureMode && (absX > 6 || absY > 6)) {
            this.mobileGestureMode = absX > absY ? 'horizontal' : 'vertical';
            this.ignoreClickToggle = true;
            this.resetTapTracking();
          }

          if (this.mobileGestureMode === 'horizontal') {
            event.preventDefault();
            this.clearDialogGestureStyles();
            this.updateHorizontalSwipe(this.mobileGestureDeltaX);
            return;
          }

          if (this.mobileGestureMode === 'vertical') {
            event.preventDefault();
            this.cleanupSwipePreview();
            this.updateVerticalDismiss(this.mobileGestureDeltaY);
            return;
          }
        }

        if (this.dragPointerId !== event.pointerId || this.scale <= 1 || this.isPinching) return;

        event.preventDefault();

        const deltaX = event.clientX - this.dragStartX;
        const deltaY = event.clientY - this.dragStartY;

        if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
          this.dragMoved = true;
          this.ignoreClickToggle = true;
          this.resetTapTracking();
        }

        this.translateX = this.dragOriginX + deltaX;
        this.translateY = this.dragOriginY + deltaY;

        this.renderTransform(false);
      }

      handlePointerUp(event) {
        const wasDragPointer = this.dragPointerId === event.pointerId;
        const hadMobileGesture = this.mobileGesturePointerId === event.pointerId;
        const gestureMode = this.mobileGestureMode;
        const deltaX = this.mobileGestureDeltaX;
        const deltaY = this.mobileGestureDeltaY;

        const wasShortMouseClick =
          event.pointerType === 'mouse' &&
          wasDragPointer &&
          !this.dragMoved &&
          !this.isPinching &&
          this.pointerDownScale > 1;

        const wasSimpleTouchTap =
          event.pointerType !== 'mouse' &&
          !this.isPinching &&
          !this.dragMoved &&
          (
            (hadMobileGesture && !gestureMode && Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) ||
            (wasDragPointer && this.pointerDownScale > 1)
          );

        const shouldNavigateBySwipe =
          hadMobileGesture &&
          gestureMode === 'horizontal' &&
          Math.abs(deltaX) > this.getHorizontalSwipeThreshold() &&
          Math.abs(deltaX) > Math.abs(deltaY);

        const shouldDismissBySwipe =
          hadMobileGesture &&
          gestureMode === 'vertical' &&
          Math.abs(deltaY) > this.getVerticalDismissThreshold() &&
          Math.abs(deltaY) > Math.abs(deltaX);

        this.pointers.delete(event.pointerId);

        if (this.dragPointerId === event.pointerId) {
          this.dragPointerId = null;
        }

        if (this.pointers.size < 2) {
          this.isPinching = false;
          this.pinchStartDistance = 0;
          this.pinchStartScale = this.scale;
        }

        const img = this.getActiveImage();
        img?.classList.remove('is-dragging');

        this.content?.releasePointerCapture?.(event.pointerId);

        if (hadMobileGesture && gestureMode === 'horizontal') {
          event.preventDefault();
          event.stopPropagation();
          this.resetTapTracking();
          this.finishHorizontalSwipe(shouldNavigateBySwipe);
          this.resetMobileGestureTracking();
          return;
        }

        if (hadMobileGesture && gestureMode === 'vertical') {
          event.preventDefault();
          event.stopPropagation();
          this.resetTapTracking();
          this.finishVerticalDismiss(shouldDismissBySwipe);
          this.resetMobileGestureTracking();
          return;
        }

        if (hadMobileGesture) {
          this.resetMobileGestureTracking();
          this.cleanupSwipePreview();
          this.clearDialogGestureStyles();
        }

        if (wasSimpleTouchTap) {
          event.preventDefault();
          event.stopPropagation();

          if (this.handlePossibleDoubleTap(event)) {
            return;
          }
        }

        if (wasShortMouseClick) {
          event.preventDefault();
          event.stopPropagation();
          this.pointerHandledClickToggle = true;
          this.ignoreClickToggle = false;
          this.setZoom(1, true);
        }
      }
    }
  );
}